/**
 * ModuleTabs (P126 / brand P2.9).
 *
 * Persistent in-module section tabs, rendered under the
 * ModuleBreadcrumb. The breadcrumb answers "where am I" (the trail);
 * these tabs answer "where else in this module" (the siblings) -- so a
 * user two levels deep isn't navigating by breadcrumb alone.
 *
 * Section tabs, NOT a side rail: v2 has no side rails and the chrome is
 * deliberately horizontal (3-module top nav + breadcrumb). Tabs keep
 * that one paradigm.
 *
 * Scope: the active top-nav module (first path segment). Curated to the
 * ~5-8 destinations a user revisits, not every route -- the module home
 * + admin browser still reach the long tail. Active = the longest-prefix
 * match, so a leaf route (e.g. /twin/findings/:id) highlights its
 * section. Renders nothing for `/`, the board/search/getting-started
 * singletons, or the full-bleed views (AppShell skips the whole chrome
 * band there).
 *
 * Lives in shared/layout (sibling to ModuleBreadcrumb) per the eslint
 * module-boundary rule. Add a tab = one line in MODULE_TABS.
 */
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import type { MarkName } from "shared/icons/marks.generated";

interface TabDef {
  /**
   * The catalogue key for the tab's name.
   *
   * It was a hard-coded English string. The eslint rule that keeps copy
   * in the catalogue reads literals in JSX CHILDREN, and these are data
   * in a record -- so the navigation chrome on every page of the
   * product was never translatable, and nobody noticed through two
   * i18n sweeps. Found by asking why a translation slice covering this
   * component produced no rows.
   */
  labelKey: string;
  to: string;
  /** P283.2: the section's Marsad Mark (Overview reuses the module mark). */
  icon: MarkName;
  /** ROUND 15 H14 -- a tab for the seat that runs the instrument. A
   *  service owner had the Field exam tab in his strip and nothing on
   *  the page that was his to answer. */
  operatorsOnly?: boolean;
  /** Round 16 D22 -- realm roles that read an operators' tab too. The
   *  CRO followed the Field exam's coverage chip for a round and lost
   *  the tab when it became operators-only; she reads it, she does not
   *  run it. */
  alsoReadBy?: readonly string[];
  /** Round 16 G13 -- a register only an administrator may read. */
  adminOnly?: boolean;
}

const MODULE_TABS: Record<string, TabDef[]> = {
  // P272.1: the UX-2 walk found the daily ops surfaces (war-room,
  // fragility, telemetry, drills) missing from this strip -- reachable
  // only by tile-hunting. Added; the two near-orphans the persona matrix
  // flagged (Channels, Journeys) demoted to Twin-home tiles to keep the
  // strip scannable.
  twin: [
    { labelKey: "nav.tab.twin", to: "/twin", icon: "twin" },
    { labelKey: "nav.tab.twin__cascade", to: "/twin/cascade", icon: "cascade" },
    {
      labelKey: "nav.tab.twin__constellation",
      to: "/twin/constellation",
      icon: "constellation",
    },
    { labelKey: "nav.tab.twin__capmap", to: "/twin/capmap", icon: "capability-map" },
    { labelKey: "nav.tab.twin__war-room", to: "/twin/war-room", icon: "war-room" },
    {
      labelKey: "nav.tab.twin__fragility",
      to: "/twin/fragility",
      icon: "fragility-radar",
    },
    { labelKey: "nav.tab.twin__telemetry", to: "/twin/telemetry", icon: "telemetry" },
    { labelKey: "nav.tab.twin__scenarios", to: "/twin/scenarios", icon: "scenario" },
    {
      labelKey: "nav.tab.twin__test-runs",
      to: "/twin/test-runs",
      icon: "drill",
    },
    { labelKey: "nav.tab.twin__drills", to: "/twin/drills", icon: "drill" },
    /* Round 16 O2 -- reports were reachable only through a finding. */
    {
      labelKey: "nav.tab.twin__incident-reports",
      to: "/twin/incident-reports",
      icon: "finding",
    },
    {
      labelKey: "nav.tab.twin__mvb-candidates",
      to: "/twin/mvb-candidates",
      icon: "mvb",
    },
    { labelKey: "nav.tab.twin__ibs-admission", to: "/twin/ibs-admission", icon: "ibs" },
    { labelKey: "nav.tab.twin__cifs", to: "/twin/cifs", icon: "ibs" },
    { labelKey: "nav.tab.twin__changes", to: "/twin/changes", icon: "journey" },
    { labelKey: "nav.tab.twin__ibs-portfolio", to: "/twin/ibs-portfolio", icon: "ibs" },
    { labelKey: "nav.tab.twin__ibs-groups", to: "/twin/ibs-groups", icon: "ibs" },
    /* Round 11 C4 -- the field-exam readiness page was typed-only.
       "20 of 28 supervisory checks flagged" is the number a compliance
       analyst is asked about, and it was on no route she could reach
       from her own navigation. */
    {
      labelKey: "nav.tab.twin__field-exam",
      to: "/twin/field-exam",
      icon: "obligation",
      operatorsOnly: true,
      alsoReadBy: ["cro"],
    },
  ],
  framework: [
    { labelKey: "nav.tab.framework", to: "/framework", icon: "framework" },
    {
      labelKey: "nav.tab.framework__assessments",
      to: "/framework/assessments",
      icon: "framework",
    },
    {
      labelKey: "nav.tab.framework__letters",
      to: "/framework/letters",
      icon: "obligation",
    },
    // Round 16 G8 -- the quarter's attestations had no tab of their own.
    {
      labelKey: "nav.tab.framework__attestations",
      to: "/framework/attestations",
      icon: "attestation",
    },
    {
      labelKey: "nav.tab.framework__queue__preparer",
      to: "/framework/queue/preparer",
      icon: "dual-signoff",
    },
    {
      labelKey: "nav.tab.framework__queue__reviewer",
      to: "/framework/queue/reviewer",
      icon: "dual-signoff",
    },
    {
      labelKey: "nav.tab.framework__queue__approver",
      to: "/framework/queue/approver",
      icon: "dual-signoff",
    },
  ],
  admin: [
    { labelKey: "nav.tab.admin", to: "/admin", icon: "console" },
    { labelKey: "nav.tab.admin__users", to: "/admin/users", icon: "dual-signoff" },
    { labelKey: "nav.tab.admin__setup", to: "/admin/setup", icon: "journey" },
    {
      labelKey: "nav.tab.admin__audit-events",
      to: "/admin/audit-events",
      icon: "hash-chain",
    },
    {
      labelKey: "nav.tab.admin__connectors",
      to: "/admin/connectors",
      icon: "telemetry",
    },
    { labelKey: "nav.tab.admin__settings", to: "/admin/settings", icon: "console" },
    {
      labelKey: "nav.tab.admin__service-accounts",
      to: "/admin/service-accounts",
      icon: "verifier",
    },
    {
      labelKey: "nav.tab.admin__data-dictionary",
      to: "/admin/data-dictionary",
      icon: "obligation",
    },
    {
      labelKey: "nav.tab.admin__data-import",
      to: "/admin/data-import",
      icon: "document-locker",
    },
    /* Round 16 H30 -- granting an examiner access was a tile and no tab,
       so the room a supervisor's visit starts from was off the strip. */
    {
      labelKey: "nav.tab.admin__examiner-grants",
      to: "/admin/examiner-grants",
      icon: "dual-signoff",
    },
  ],
  foundation: [
    { labelKey: "nav.tab.foundation", to: "/foundation", icon: "foundation" },
    /* Round 16 G13 -- the registers behind these tabs admit the
       resilience desk and administrators (capabilities: administrators
       only), and the treasurer was offered them and refused. */
    {
      labelKey: "nav.tab.foundation__admin__ibses",
      to: "/foundation/admin/ibses",
      icon: "ibs",
      operatorsOnly: true,
    },
    {
      labelKey: "nav.tab.foundation__admin__services",
      to: "/foundation/admin/services",
      icon: "constellation",
      operatorsOnly: true,
    },
    {
      labelKey: "nav.tab.foundation__admin__capabilities",
      to: "/foundation/admin/capabilities",
      icon: "capability-map",
      adminOnly: true,
    },
    { labelKey: "nav.tab.foundation__mvb", to: "/foundation/mvb", icon: "mvb" },
    {
      labelKey: "nav.tab.foundation__admin__risk-appetite-statements",
      to: "/foundation/admin/risk-appetite-statements",
      icon: "tolerance-clock",
    },
  ],
};

/**
 * The active tab's `to`, by longest-prefix match. A tab matches when the
 * pathname IS its `to` or sits under it (`to/...`). Among matches the
 * longest `to` wins, so the Overview tab (`/twin`) only activates on the
 * module root, not on `/twin/cascade`. Returns null when nothing matches.
 */
export function activeTabTo(
  tabs: readonly { to: string }[],
  pathname: string,
): string | null {
  let best: string | null = null;
  for (const tab of tabs) {
    if (pathname === tab.to || pathname.startsWith(tab.to + "/")) {
      if (best === null || tab.to.length > best.length) best = tab.to;
    }
  }
  return best;
}

export function ModuleTabs() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const me = useMe();
  const moduleKey = pathname.split("/")[1] ?? "";
  const tabs = MODULE_TABS[moduleKey];
  if (!tabs) return null;
  // E1 -- a preparer who lands on an admin URL used to get the full
  // admin strip wrapped around a denial: nine destinations, every one
  // of which would refuse them. Offering navigation into rooms you
  // cannot enter is worse than offering none.
  if (moduleKey === "admin" && me.data?.is_admin !== true) return null;
  // ROUND 15 H14 -- and a tab whose page is another seat's instrument
  // stays out of a strip it would only decorate.
  const visible = tabs.filter(
    (tab) =>
      (!tab.operatorsOnly ||
        canRunOperations(me.data) ||
        (tab.alsoReadBy ?? []).some((r) => (me.data?.realm_roles ?? []).includes(r))) &&
      (!tab.adminOnly || me.data?.is_admin === true),
  );

  const active = activeTabTo(tabs, pathname);

  return (
    <nav
      data-testid="module-tabs"
      aria-label="Module sections"
      // Wrap rather than scroll: a horizontally-scrolling strip with
      // no cue hides tabs from anyone who does not think to drag it,
      // and six of the Twin module's twelve were hidden that way.
      className="mt-1 mb-5 flex flex-wrap gap-x-1 gap-y-0 border-b border-paper-2"
    >
      {visible.map((tab) => {
        const isActive = tab.to === active;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            data-testid="module-tab"
            data-to={tab.to}
            data-active={isActive ? "true" : "false"}
            aria-current={isActive ? "page" : undefined}
            className={[
              "-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition",
              isActive
                ? "border-brand-brass text-brand-brassDark"
                : "border-transparent text-slate-500 hover:text-brand-brassDark",
            ].join(" ")}
          >
            <MarsadIcon name={tab.icon} size={15} />
            {t(tab.labelKey)}
          </Link>
        );
      })}
    </nav>
  );
}
